import { File, FileAudio, FileSpreadsheet, FileText, FileVideo, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { checkIsImage } from '../../utils/isImage';

const AUDIO_EXTENSIONS = ['mp3', 'm4a', 'amr', 'mpeg'];
const VIDEO_EXTENSIONS = ['mp4', 'mov'];
const SHEET_EXTENSIONS = ['csv', 'xlsx', 'xls'];
const TEXT_EXTENSIONS = ['pdf', 'doc', 'docx', 'txt'];

export const getExtension = (name = '') => (name.includes('.') ? name.split('.').pop().toLowerCase() : '');

export const getKind = ({ type = '', name = '' }) => {
  const extension = getExtension(name);
  if (type.startsWith('audio/') || AUDIO_EXTENSIONS.includes(extension)) return 'audio';
  if (type.startsWith('video/') || VIDEO_EXTENSIONS.includes(extension)) return 'video';
  if (SHEET_EXTENSIONS.includes(extension)) return 'sheet';
  if (TEXT_EXTENSIONS.includes(extension)) return 'text';
  return 'file';
};

export const KIND_ICONS = {
  audio: FileAudio,
  video: FileVideo,
  sheet: FileSpreadsheet,
  text: FileText,
  file: File,
};

export const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDuration = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

const useMediaDuration = (src, enabled) => {
  const [duration, setDuration] = useState(null);

  useEffect(() => {
    if (!enabled || !src) return undefined;

    const media = document.createElement('audio');
    const onLoaded = () => setDuration(media.duration);
    media.preload = 'metadata';
    media.addEventListener('loadedmetadata', onLoaded);
    media.src = src;

    return () => {
      media.removeEventListener('loadedmetadata', onLoaded);
      media.removeAttribute('src');
    };
  }, [src, enabled]);

  return duration;
};

const ImagePreview = ({ attachment, removeAttachment }) => {
  const kind = attachment ? getKind(attachment) : 'file';
  const isImage =
    !!attachment &&
    (attachment.type?.startsWith('image/') ||
      checkIsImage(attachment.path) ||
      checkIsImage(attachment.name) ||
      checkIsImage(attachment.mediaUrl));
  const src = attachment?.preview || attachment?.mediaUrl;
  const duration = useMediaDuration(src, kind === 'audio' || kind === 'video');

  if (!attachment) {
    return null;
  }

  const { name, size, loading, uploadProgress } = attachment;
  const Icon = KIND_ICONS[kind];
  const hasProgress = Number.isFinite(uploadProgress);

  const details = loading
    ? `Uploading${hasProgress ? ` ${Math.round(uploadProgress * 100)}%` : '…'}`
    : [getExtension(name).toUpperCase(), formatDuration(duration), formatSize(size)]
        .filter(Boolean)
        .join(' · ');

  return (
    <div className="relative flex w-64 max-w-full items-center gap-3 overflow-hidden rounded-lg border bg-card py-2 pl-2 pr-9 text-card-foreground shadow-sm">
      {isImage ? (
        <img alt="" src={src} className="h-11 w-11 shrink-0 rounded-md bg-muted object-cover" />
      ) : (
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium leading-tight" title={name}>
          {name}
        </p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground" aria-live="polite">
          {details}
        </p>
      </div>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute right-1 top-1 h-7 w-7 text-muted-foreground max-md:right-0 max-md:top-0 max-md:h-11 max-md:w-11"
        aria-label={`Remove ${name}`}
        onClick={() => removeAttachment(attachment)}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </Button>

      {loading && (
        <Progress
          value={hasProgress ? uploadProgress * 100 : 100}
          aria-label={`Uploading ${name}`}
          className={`absolute inset-x-0 bottom-0 h-1 rounded-none ${hasProgress ? '' : 'animate-pulse'}`}
        />
      )}
    </div>
  );
};

export default ImagePreview;
